/**
 * Job Value Band helpers (ER-11783).
 *
 * A band is derived from the account's configured band count and the global
 * Total Score maximum — it is never persisted and has no name or number. These
 * helpers mirror the backend arithmetic exactly:
 *
 *   lower(i) = ceil(i * max / n)
 *   upper(i) = ceil((i + 1) * max / n) - 1   (upper(n - 1) = max)
 *
 * Both ends are inclusive and the bands tile 0..max with no gap or overlap.
 */

/** Fewest bands an account may divide the Total Score range into. */
export const BAND_COUNT_MIN = 2;

/** Most bands an account may divide the Total Score range into. */
export const BAND_COUNT_MAX = 10;

/** Band count every account is seeded with. */
export const BAND_COUNT_DEFAULT = 5;

/**
 * True when `value` is a whole number inside the allowed band-count range.
 * Strings from a number input are accepted; booleans, blanks and decimals are not.
 */
export function isValidBandCount(value) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") {
    return false;
  }
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= BAND_COUNT_MIN && numeric <= BAND_COUNT_MAX;
}

/**
 * Derive the ordered band list for `bandCount` bands over `totalMaxPoints`.
 * Returns [] when either input is not a usable whole number, so callers can
 * render an empty preview while an invalid count is being typed.
 */
export function deriveJobValueBands(bandCount, totalMaxPoints) {
  if (!isValidBandCount(bandCount)) {
    return [];
  }
  const count = Number(bandCount);
  const max = Number(totalMaxPoints);
  if (!Number.isInteger(max) || max <= 0) {
    return [];
  }
  return Array.from({ length: count }, (_, index) => {
    const minScore = Math.ceil((index * max) / count);
    const maxScore =
      index === count - 1 ? max : Math.ceil(((index + 1) * max) / count) - 1;
    return { min_score: minScore, max_score: maxScore };
  });
}

/** Display form of a band: its inclusive point range only, e.g. "30–43". */
export function formatBandRange(band) {
  if (!band || band.min_score === null || band.min_score === undefined) {
    return "";
  }
  if (band.max_score === null || band.max_score === undefined) {
    return "";
  }
  return `${band.min_score}–${band.max_score}`;
}

/** The band containing `score`, or null when the score is outside every band. */
export function bandForScore(score, bands) {
  const numeric = Number(score);
  if (!Number.isFinite(numeric) || !Array.isArray(bands)) {
    return null;
  }
  return (
    bands.find(
      (band) =>
        band &&
        numeric >= Number(band.min_score) &&
        numeric <= Number(band.max_score)
    ) || null
  );
}
